import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Picture } from "@/components/wpl/image/src/index";
import { Link } from "@/libraries/design/components";

import { getIsGwp10027NonprofitPricingPage } from "../feature-gates.ts";

import { EligibilityListGwp7869 } from "./eligibility-list.tsx";

const eligibleContainerCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center"
});

const headerCss = css({
    fontSize: "32px",
    lineHeight: "38px",
    fontWeight: 800,
    margin: "0 0 14px 0",
    [media.above.sm]: {
        fontSize: "48px",
        lineHeight: "48px",
        margin: "0 0 16px 0"
    }
});

const subtitleCss = css({
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    margin: "0 auto 10px",
    [media.above.sm]: {
        fontSize: "20px",
        lineHeight: "28px",
        maxWidth: "768px",
        margin: "0 auto 20px"
    },
    [media.above.md]: {
        fontSize: "28px",
        lineHeight: "40px",
        maxWidth: "100%"
    }
});

const ctaBlockCss = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    margin: "50px 0 0",
    backgroundSize: "140% auto",
    backgroundRepeat: "no-repeat, no-repeat",
    padding: "55px 16px",
    position: "relative",
    backgroundImage:
        'url("https://dam-cdn.atl.orangelogic.com/AssetLink/xo68oe60v01q41q2ac4j00105878i48l.webp")',
    [media.only.sm]: {
        backgroundPosition: "left top , right -75px bottom",
        backgroundSize: "699px auto",
        padding: "96px 16px",
        height: "218px"
    },
    [media.above.sm]: {
        backgroundImage:
            'url("https://dam-cdn.atl.orangelogic.com/AssetLink/04451t30ps6lpg487gb3ml40mcmb3rj6.webp")',
        backgroundPosition: "center",
        backgroundSize: "729px auto"
    }
});

const ctaBlockContentCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [media.above.sm]: {
        padding: "16px 16px 32px"
    }
});

const ctaBlockTitleCss = css({
    fontSize: "24px",
    lineHeight: "30px",
    margin: "0"
});

const ctaBlockDetailCss = css({
    fontSize: "16px",
    lineHeight: "24px"
});

const imageCss = css({
    maxWidth: "112px",
    maxHeight: "116px",
    position: "absolute",
    insetInlineEnd: "-5%",
    insetBlockEnd: "-10%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["img"]: {
        width: "75%",
        height: "75%"
    },
    [media.only.sm]: {
        insetInlineEnd: "15%",
        insetBlockEnd: "10%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["img"]: {
            width: "100%",
            height: "100%"
        }
    },
    [media.above.md]: {
        insetInlineEnd: "20%",
        insetBlockEnd: "15%"
    }
});
export const EligibleSection = () => {
    const intl = useIntl();

    const isGwp10027 = getIsGwp10027NonprofitPricingPage();

    const learnMore = intl.formatMessage({
        id: "NonprofitPage.eligible.cta.content.link",
        defaultMessage: "Learn more"
    });

    return (
        <div css={eligibleContainerCss}>
            <h2 css={headerCss}>
                <FormattedMessage
                    id="NonprofitPage.eligible.header"
                    defaultMessage="Who’s eligible"
                />
            </h2>
            <h3 css={subtitleCss}>
                {isGwp10027 ? (
                    <FormattedMessage
                        id="NonprofitPage.eligible.subtitle.gwp10027"
                        defaultMessage="50% - 100% off discounts are available for nonprofits and social enterprises that are:"
                    />
                ) : (
                    <FormattedMessage
                        id="NonprofitPage.eligible.subtitle.gwp7869"
                        defaultMessage="Discounts on Atlassian Cloud apps are available to nonprofits and social enterprises that are:"
                    />
                )}
            </h3>

            <EligibilityListGwp7869 />

            <div css={ctaBlockCss}>
                <div css={ctaBlockContentCss}>
                    <h3 css={ctaBlockTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.eligible.cta.title"
                            defaultMessage="Need more info?"
                        />
                    </h3>
                    <p css={ctaBlockDetailCss}>
                        <FormattedMessage
                            id="NonprofitPage.eligible.cta.content"
                            defaultMessage="Get the answers for your most frequently asked eligibility questions."
                        />
                    </p>
                    <Link
                        text={learnMore}
                        aria-label={learnMore}
                        href="#faq"
                        type="primary"
                        dataEventProps={{
                            "data-event-component": "linkButton",
                            // @ts-ignore
                            "data-event-container": "Eligibility section",
                            "data-event": "clicked",
                            "data-product-key": "nonProfitCalculator"
                        }}
                    />
                    <div css={imageCss}>
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8fhb444317uecxj0n4dy144u8atr503c.webp",
                                alt: ""
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
EligibleSection.displayName = "EligibleSection";
